import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Container, Grid, Typography, Stack, CircularProgress, Alert } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';
import useVrackApi from '../hooks/useVrackApi';
import Page from '../components/Page';
import BillingTable from '../components/billing/BillingTable';
import { formatMonthParam } from '../components/billing/MonthPicker';

// Debounce delay (ms) for the search keyword before filtering the list
const SEARCH_DEBOUNCE_MS = 400;

// Map the snake_case `CompanyResponse` shape returned by
// /billing/companies/invoices into the camelCase shape the table already
// consumes. Defensive against the endpoint returning either a bare array or an
// object with an `items` field.
const resolveCompanyItems = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.items)) return response.items;
  return [];
};

const mapCompaniesResponse = (response) =>
  resolveCompanyItems(response).map((item) => ({
    id: item.company_id,
    name: item.company_name || '-',
    type: item.type,
    contactEmail: item.contact_email,
    teamsCount: item.team_count ?? 0
  }));

// Case-insensitive comparator used for client-side sort. Falls back to
// string comparison on `id` so undefined values never crash the sort.
const compareBy = (key) => (a, b) => {
  const av = a?.[key];
  const bv = b?.[key];
  if (av === bv) return 0;
  if (av === undefined || av === null) return 1;
  if (bv === undefined || bv === null) return -1;
  if (typeof av === 'number' && typeof bv === 'number') return av - bv;
  return String(av).localeCompare(String(bv));
};

export default function Billing() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterName, setFilterName] = useState('');
  const [keyword, setKeyword] = useState('');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [companies, setCompanies] = useState([]);

  // The list page always reflects the current billing month — there's no month
  // picker here. Capture the current month once on mount so it doesn't drift
  // mid-session, and pass it through the same `month` query param the per-company
  // billing-report endpoint uses.
  const [billingMonth] = useState(() => formatMonthParam(new Date()) || '');

  const { loading, error, fetchData } = useVrackApi('get', '/billing/companies/invoices');

  const fetchCompanies = useCallback(
    async (signal) => {
      try {
        const response = await fetchData({ params: { month: billingMonth }, signal });
        if (!signal?.aborted) {
          setCompanies(mapCompaniesResponse(response));
        }
      } catch (err) {
        if (signal?.aborted || err?.code === 'ERR_CANCELED') return; // ignore cancelled requests
        console.error('Failed to fetch companies:', err);
        setCompanies([]);
      }
    },
    [fetchData, billingMonth]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchCompanies(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetch = useCallback(() => fetchCompanies(undefined), [fetchCompanies]);

  // Debounce the search input so we only re-filter after the user stops typing
  const debounceRef = useRef(null);
  const handleFilterByName = (event) => {
    const { value } = event.target;
    setFilterName(value);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setKeyword(value);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
  };

  // Cleanup debounce timer on unmount
  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    },
    []
  );

  // Filter + sort the full list client-side. The /billing/companies/invoices
  // endpoint returns a flat array with no pagination/sort/search params, so all
  // of this lives here.
  const filteredCompanies = useMemo(() => {
    if (!keyword) return companies;
    const searchLower = keyword.toLowerCase();
    return companies.filter(
      (company) =>
        company.name.toLowerCase().includes(searchLower) ||
        company.id.toLowerCase().includes(searchLower) ||
        (company.contactEmail || '').toLowerCase().includes(searchLower)
    );
  }, [companies, keyword]);

  const sortedCompanies = useMemo(() => {
    const sorted = [...filteredCompanies].sort(compareBy(orderBy));
    return order === 'desc' ? sorted.reverse() : sorted;
  }, [filteredCompanies, orderBy, order]);

  const totalCount = sortedCompanies.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  // MUI TablePagination is 0-based; keep our state 1-based to match the previous
  // shape the table was built against, then derive the slice using 0-based math.
  const pageRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sortedCompanies.slice(start, start + rowsPerPage);
  }, [sortedCompanies, page, rowsPerPage]);

  // If only one company exists in the underlying data, jump straight to its
  // billing report. We base the decision on the raw, pre-filter `companies`
  // count rather than `totalCount` (which reflects the visible list after the
  // search filter), so typing into the search bar never auto-redirects the
  // user away from the list. Also skip the redirect while a search filter is
  // active for the same reason — even if the user clears the filter back to
  // empty later, the redirect would have already been suppressed.
  const redirectedRef = useRef(false);
  useEffect(() => {
    if (loading || error) return;
    if (keyword.trim() !== '') return;
    if (companies.length !== 1 || redirectedRef.current) return;
    const only = companies[0];
    if (only?.id) {
      redirectedRef.current = true;
      navigate(`/billing/company/${only.id}`, { replace: true });
    }
  }, [companies, keyword, loading, error, navigate]);

  const handleChangePage = (event, newPage) => {
    // MUI TablePagination is 0-based; our internal state stays 1-based.
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(1);
  };

  // While the single-company redirect is pending, render a spinner rather
  // than the table — a single row is never useful here, and `return null`
  // leaves the dashboard layout visibly empty until navigation completes.
  // Use the raw, pre-filter company count so a search that narrows the list
  // to one row doesn't blank out the table while the redirect is skipped.
  if (!loading && !error && keyword.trim() === '' && companies.length === 1) {
    return (
      <Page title="Billing | Virtual Racks">
        <Container maxWidth={false}>
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Stack>
        </Container>
      </Page>
    );
  }

  return (
    <Page title="Billing | Virtual Racks">
      <Container maxWidth={false}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack spacing={0}>
              <Typography variant="h4">Billing</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Select a company to review its monthly billing report and team-level cost
                allocation.
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            {error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to load companies.{' '}
                <Typography
                  component="span"
                  sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'primary.main' }}
                  onClick={refetch}
                >
                  Retry
                </Typography>
              </Alert>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  {loading && companies.length === 0 ? (
                    <Stack alignItems="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </Stack>
                  ) : (
                    <BillingTable
                      companies={pageRows}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      totalCount={totalCount}
                      totalPages={totalPages}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      filterName={filterName}
                      onFilterName={handleFilterByName}
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={handleRequestSort}
                    />
                  )}
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
